/**
 * rings-paint — canvas-2D primitives for the three-ring meter
 * renderer.
 *
 * Carved out of `rings.ts` per AH-W5-a.2 / FA-1 §3.3: the meter's
 * draw primitives (arc stroke, sweep gradient, phantom-track pass,
 * active-phase fan-out) carry a Canvas 2D dependency but no public
 * draw* API surface — they belong on their own seam so the public
 * `rings.ts` reads as the renderer's entry-point + factory, not as
 * a mixed bag of helpers.
 *
 * Reads color math from `rings-color.ts`; reads `MeterConfig` /
 * `RingSet` types from `rings.ts`. The public draw* API (drawRings,
 * drawRingsCrossfade, drawAllRings, drawMilestoneBloom) imports
 * from THIS module — never the reverse.
 */

import type { RingSet } from "./rings";
import { type PhaseKey, mixHex, hexToRgba } from "./rings-color";

/**
 * Narrow an arbitrary state name string to a `PhaseKey`, or null if
 * it isn't one of the three named phases. Used at every paint entry
 * point that gates on phase identity (e.g. `drawRings` bailing on
 * `complete` / `ready` / `undefined`).
 */
export function asPhaseKey(name: string | undefined): PhaseKey | null {
    return name === "ping" || name === "download" || name === "upload"
        ? (name as PhaseKey)
        : null;
}

/**
 * Stroke a single arc on `ctx` from `startAngle` to `startAngle + (endAngle − startAngle) * t`
 * at `radius` with `lineWidth` and `color`.
 *
 * Optional shadow opts paint a glow underlay in one pass; `alpha`
 * multiplies the global pass opacity (the lever the active-phase
 * paint pulls so glow + crisp passes scale together).
 */
export function strokeArc(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    color: string | CanvasGradient,
    lineWidth: number,
    t: number,
    opts: {
        shadowColor?: string;
        shadowBlur?: number;
        shadowOffsetY?: number;
        alpha?: number;
    } = {},
): void {
    if (t <= 0) return;
    ctx.save();
    ctx.globalAlpha = opts.alpha ?? 1;
    ctx.lineCap = "round";
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = color;
    if (opts.shadowColor) {
        ctx.shadowColor = opts.shadowColor;
        ctx.shadowBlur = opts.shadowBlur ?? 0;
        if (opts.shadowOffsetY !== undefined) {
            ctx.shadowOffsetY = opts.shadowOffsetY;
        }
    }
    const span = (endAngle - startAngle) * t;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, startAngle, startAngle + span, false);
    ctx.stroke();
    ctx.restore();
}

/**
 * Build a `CanvasGradient` that sweeps between two endpoints of an
 * arc — the gradient runs along the chord between (startAngle) and
 * (endAngle) on `radius`, with `colorA` at both ends and `colorB`
 * peaking at the midpoint. Used to add restrained rotational motion
 * inside each ring's 3-shade ladder pass.
 */
export function sweepGradient(
    ctx: CanvasRenderingContext2D,
    originX: number,
    originY: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    colorA: string,
    colorB: string,
): CanvasGradient {
    const x0 = originX + Math.cos(startAngle) * radius;
    const y0 = originY + Math.sin(startAngle) * radius;
    const x1 = originX + Math.cos(endAngle) * radius;
    const y1 = originY + Math.sin(endAngle) * radius;
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    g.addColorStop(0, colorA);
    g.addColorStop(0.5, colorB);
    g.addColorStop(1, colorA);
    return g;
}

/**
 * Phantom tracks — one canon neutral stroke per ring at low alpha. Flat,
 * no depth pass: the rings read as a clean instrument substrate.
 */
export function paintPhantomTracks(
    ctx: CanvasRenderingContext2D,
    rings: RingSet,
): void {
    const { originX, originY, startAngle, endAngle } = rings.cfg;
    const trackAlpha = 0.22;
    for (const ring of rings.specs) {
        strokeArc(
            ctx,
            originX,
            originY,
            ring.radius,
            startAngle,
            endAngle,
            rings.bgColor,
            ring.lineWidth,
            1,
            { alpha: trackAlpha },
        );
    }
}

/**
 * Paint one phase's 3-ring monochrome ladder at fill `t`.
 *
 * Each ring gets a sweep gradient between softened neighbor shades to add
 * restrained rotational motion; a glow underlay (pass 1) uses the phase's
 * mid shade, then a crisp stroke (pass 2) lands on top. This is the single
 * active-phase paint path — `drawRings` calls it once, `drawAllRings` calls
 * it per named phase.
 *
 * `alphaScale` (default 1) multiplies BOTH stroke passes' opacity — the
 * lever the one-sided run-start / run-end cross-fade pulls so the active
 * rings fade UP from / DOWN to the phantom-track substrate that
 * `paintPhantomTracks` already painted beneath them (AG-A4-EDGE-ASYMMETRY).
 */
export function paintActivePhase(
    ctx: CanvasRenderingContext2D,
    rings: RingSet,
    phase: PhaseKey,
    t: number,
    ladderOverride?: [string, string, string],
    alphaScale = 1,
): void {
    const { originX, originY, startAngle, endAngle, blurMargin } = rings.cfg;
    // The ladder is the phase's canon triple, UNLESS a cross-fade
    // override is supplied (W5-L2 boundary handoff) — then the rings
    // paint an interpolated hue so they glide with `--phase-color`.
    const ladder = ladderOverride ?? rings.stops[phase];
    const alpha = Math.min(1, Math.max(0, alphaScale));
    const glowColor = hexToRgba(ladder[1], 0.18 * alpha);
    const glowBlur = Math.max(1, blurMargin * 0.55);

    for (const ring of rings.specs) {
        const idx = ring.colorIdx;
        const baseColor = ladder[idx];
        const neighborA = mixHex(
            baseColor,
            ladder[Math.max(0, idx - 1)] ?? baseColor,
            0.22,
        );
        const neighborB = mixHex(
            baseColor,
            ladder[Math.min(2, idx + 1)] ?? baseColor,
            0.22,
        );
        const gradient = sweepGradient(
            ctx,
            originX,
            originY,
            ring.radius,
            startAngle,
            endAngle,
            neighborA,
            neighborB,
        );
        // First pass: glow underlay. Second pass: crisp stroke on top.
        // Both passes scale by `alpha` so a one-sided handoff fades the
        // whole ring up from / down to the phantom substrate.
        strokeArc(
            ctx,
            originX,
            originY,
            ring.radius,
            startAngle,
            endAngle,
            gradient,
            ring.lineWidth,
            t,
            {
                shadowColor: glowColor,
                shadowBlur: glowBlur,
                alpha: 0.55 * alpha,
            },
        );
        strokeArc(
            ctx,
            originX,
            originY,
            ring.radius,
            startAngle,
            endAngle,
            gradient,
            ring.lineWidth,
            t,
            { alpha: 0.92 * alpha },
        );
    }
}
